<script setup lang="ts">
import Container from '@/components/www/primitives/Container.vue'
import ResourceMenuIcon from './ResourceMenuIcon.vue'
import {featuredResource, resourceMenuColumns} from './resourceMenu'

/**
 * Desktop Resources mega menu. Visibility is driven by the parent Header via
 * `open` — the panel handles its own open/close animation through [data-open] + hidden.
 */
defineProps<{
    open: boolean
    hiddenPanel: boolean
}>()

const emit = defineEmits<{
    'link-click': []
}>()
</script>

<template>
  <div
      id="resources-mega-menu"
      class="resources-mega"
      :data-open="open ? 'true' : 'false'"
      :hidden="hiddenPanel"
      role="region"
      aria-label="Resources"
      data-resources-mega
  >
    <div class="resources-mega__glow" aria-hidden="true"/>
    <Container width="lg" class="resources-mega__inner">
      <div class="resources-mega__layout">
        <aside class="resources-mega__intro">
          <p class="resources-mega__tagline">Research and tools<br>for the next marketplace.</p>
          <RouterLink class="resources-mega__all" to="/resources" data-resources-all @click="emit('link-click')">
            Browse all resources
            <span class="resources-mega__all-arrow" aria-hidden="true">&rarr;</span>
          </RouterLink>
        </aside>

        <div class="resources-mega__columns">
          <section
              v-for="(column, columnIndex) in resourceMenuColumns"
              :key="column.id"
              class="resources-mega__column"
              :data-resource-column="column.id"
              :style="{'--mega-col': columnIndex}"
          >
            <h2 class="resources-mega__column-title">{{ column.title }}</h2>
            <ul class="resources-mega__list" role="list">
              <li
                  v-for="(item, index) in column.items"
                  :key="item.id"
                  :style="{'--mega-i': index}"
              >
                <RouterLink
                    class="resources-mega__link"
                    :to="item.to"
                    :data-resource-link="item.id"
                    @click="emit('link-click')"
                >
                  <ResourceMenuIcon :name="item.id"/>
                  <span class="resources-mega__link-copy">
                    <span class="resources-mega__link-label">{{ item.label }}</span>
                    <span class="resources-mega__link-desc">{{ item.description }}</span>
                  </span>
                </RouterLink>
              </li>
            </ul>
          </section>
        </div>

        <RouterLink
            class="resources-mega__featured"
            :to="featuredResource.to"
            data-resource-featured
            @click="emit('link-click')"
        >
          <span class="resources-mega__featured-media">
            <img
                :src="featuredResource.image"
                :alt="featuredResource.title"
                width="640"
                height="400"
            >
          </span>
          <span class="resources-mega__featured-body">
            <span class="resources-mega__featured-eyebrow">{{ featuredResource.eyebrow }}</span>
            <span class="resources-mega__featured-title">{{ featuredResource.title }}</span>
            <span class="resources-mega__featured-desc">{{ featuredResource.description }}</span>
            <span class="resources-mega__featured-cta">
              {{ featuredResource.cta }}
              <span class="resources-mega__featured-arrow" aria-hidden="true">&rarr;</span>
            </span>
          </span>
        </RouterLink>
      </div>
    </Container>
  </div>
</template>

<style scoped>
.resources-mega {
    --mega-ease: var(--ease-out-strong);
    position: absolute;
    inset-inline: 0;
    inset-block-start: 100%;
    z-index: 45;
    width: 100%;
    overflow: hidden;
    border-block-end: 1px solid var(--border);
    box-shadow:
        0 28px 72px -36px rgba(26, 24, 20, 0.12),
        0 12px 24px -16px rgba(26, 24, 20, 0.08);
    background:
        radial-gradient(ellipse 70% 55% at 8% -10%, rgba(26, 24, 20, 0.03), transparent 58%),
        radial-gradient(ellipse 45% 40% at 92% 110%, color-mix(in srgb, var(--primary-500) 8%, transparent), transparent 55%),
        linear-gradient(180deg, var(--surface) 0%, var(--card-bg) 52%, var(--background) 100%);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-6px) scale(0.98);
    transform-origin: top center;
    transition:
        opacity 240ms var(--mega-ease),
        transform 260ms var(--mega-ease),
        visibility 240ms;
}

.resources-mega__glow {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
        linear-gradient(115deg, transparent 40%, rgba(255, 255, 255, 0.55) 50%, transparent 60%);
    opacity: 0;
    transform: translateX(-30%);
}

.resources-mega[data-open="true"] {
    z-index: 46;
    opacity: 1;
    visibility: visible;
    transform: translateY(0) scale(1);
}

.resources-mega[data-open="true"] .resources-mega__glow {
    opacity: 0.35;
    animation: resources-mega-shimmer 1.1s var(--mega-ease) 0.08s both;
}

@keyframes resources-mega-shimmer {
    from {
        transform: translateX(-30%);
        opacity: 0;
    }
    to {
        transform: translateX(35%);
        opacity: 0.2;
    }
}

.resources-mega :deep(.resources-mega__inner) {
    padding-block: 66px 40px;
}

.resources-mega__layout {
    display: grid;
    grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.35fr) minmax(0, 0.9fr);
    gap: 40px 32px;
    align-items: start;
}

.resources-mega__intro,
.resources-mega__columns,
.resources-mega__column,
.resources-mega__featured {
    min-width: 0;
}

.resources-mega__intro {
    padding-top: 4px;
}

.resources-mega__tagline {
    margin: 0 0 16px;
    font-family: var(--font-display);
    font-size: clamp(1.35rem, 1.8vw, 1.7rem);
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--text-primary);
}

.resources-mega__all {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-500);
    transition: gap 180ms var(--ease-out);
}

.resources-mega__all:hover,
.resources-mega__all:focus-visible {
    gap: 10px;
}

.resources-mega__all:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 4px;
    border-radius: 4px;
}

.resources-mega__all-arrow {
    transition: transform 180ms var(--ease-out);
}

.resources-mega__columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 32px 24px;
}

.resources-mega__column-title {
    margin: 0 0 18px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: var(--primary-500);
}

.resources-mega__list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.resources-mega[data-open="true"] .resources-mega__column {
    animation: resources-mega-item-in 0.48s var(--mega-ease) both;
    animation-delay: calc(0.04s + (var(--mega-col, 0) * 0.05s));
}

.resources-mega[data-open="true"] .resources-mega__link {
    animation: resources-mega-item-in 0.48s var(--mega-ease) both;
    animation-delay: calc(0.08s + (var(--mega-i, 0) * 0.04s));
}

.resources-mega[data-open="true"] .resources-mega__featured {
    animation: resources-mega-item-in 0.52s var(--mega-ease) both;
    animation-delay: 0.14s;
}

@keyframes resources-mega-item-in {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.resources-mega__link {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    padding: 10px 10px;
    margin-inline: -10px;
    border-radius: 10px;
    text-decoration: none;
    color: inherit;
    transition: background-color 160ms var(--ease-out);
}

.resources-mega__link-copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

@media (hover: hover) and (pointer: fine) {
    .resources-mega__link:hover,
    .resources-mega__link:focus-visible {
        background: color-mix(in srgb, var(--primary-500) 6%, transparent);
    }
}

.resources-mega__link:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
}

.resources-mega__link-label {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
}

.resources-mega__link-desc {
    font-size: 13px;
    line-height: 1.35;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.resources-mega__featured {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 16px;
    border: 1px solid var(--border);
    background: var(--surface);
    text-decoration: none;
    color: inherit;
    box-shadow: none;
    transition: transform 220ms var(--mega-ease);
}

@media (hover: hover) and (pointer: fine) {
    .resources-mega__featured:hover,
    .resources-mega__featured:focus-visible {
        transform: translateY(-2px);
    }
}

.resources-mega__featured:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 3px;
}

.resources-mega__featured-media {
    display: block;
    aspect-ratio: 16 / 10;
    overflow: hidden;
    background: var(--secondary-700);
}

.resources-mega__featured-media img {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    height: 100%;
    object-fit: cover;
    transform: scale(1.02);
    transition: transform 640ms var(--mega-ease);
}

.resources-mega__featured:hover .resources-mega__featured-media img,
.resources-mega__featured:focus-visible .resources-mega__featured-media img {
    transform: scale(1.08);
}

.resources-mega__featured-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px 16px;
}

.resources-mega__featured-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.3px;
    text-transform: uppercase;
    color: var(--primary-500);
}

.resources-mega__featured-title {
    font-family: var(--font-display);
    font-size: 1.05rem;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: -0.02em;
    color: var(--text-primary);
}

.resources-mega__featured-desc {
    font-size: 13px;
    line-height: 1.4;
    color: var(--text-secondary);
}

.resources-mega__featured-cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-500);
}

.resources-mega__featured-arrow {
    transition: transform 180ms var(--ease-out);
}

.resources-mega__featured:hover .resources-mega__featured-arrow,
.resources-mega__featured:focus-visible .resources-mega__featured-arrow {
    transform: translateX(3px);
}

@media (max-width: 1100px) {
    .resources-mega__layout {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
        gap: 32px 24px;
    }

    .resources-mega__featured {
        grid-column: 1 / -1;
        flex-direction: row;
    }

    .resources-mega__featured-media {
        width: 42%;
        aspect-ratio: 4 / 3;
        flex-shrink: 0;
    }
}

@media (max-width: 767px) {
    .resources-mega {
        display: none;
    }
}

@media (prefers-reduced-motion: reduce) {
    .resources-mega,
    .resources-mega__column,
    .resources-mega__link,
    .resources-mega__featured,
    .resources-mega__featured-media img,
    .resources-mega__glow {
        animation: none;
        transition: none;
    }

    .resources-mega__featured:hover .resources-mega__featured-media img {
        transform: none;
    }
}
</style>
